<template>
  <div v-if="copy" class="public-guide">
    <BaseImg :title="copy.heading" :bgName="heroImage" />
    <div class="guide-shell">
      <nav class="guide-breadcrumbs" :aria-label="labels.breadcrumbs">
        <router-link :to="$siteLink('/')">{{ labels.home }}</router-link>
        <span aria-hidden="true">/</span>
        <span aria-current="page">{{ copy.label }}</span>
      </nav>
      <p class="guide-intro">{{ copy.intro }}</p>
      <div class="guide-layout">
        <article class="guide-body">
          <section v-for="section in copy.sections" :key="section.heading" :id="section.id" class="guide-section">
            <h2>{{ section.heading }}</h2>
            <p>{{ section.text }}</p>
            <ul v-if="section.items">
              <li v-for="item in section.items" :key="item">{{ item }}</li>
            </ul>
          </section>
        </article>
        <aside class="guide-related" :aria-labelledby="relatedHeadingId">
          <h2 :id="relatedHeadingId">{{ labels.related }}</h2>
          <ul>
            <li v-for="link in copy.related" :key="link.path">
              <router-link :to="$siteLink(link.path)">{{ link.label }} <span aria-hidden="true">→</span></router-link>
            </li>
          </ul>
        </aside>
      </div>
    </div>
    <PublicTransferGuide v-if="guide.path === '/how-it-works'" />
    <section class="guide-faq">
      <div class="guide-shell">
        <h2>{{ labels.faq }}</h2>
        <div class="faq-items">
          <section v-for="item in copy.faq" :key="item.question" class="faq-item">
            <h3>{{ item.question }}</h3>
            <p>{{ item.answer }}</p>
          </section>
        </div>
      </div>
    </section>
    <div class="guide-shell">
      <section class="guide-sources">
        <h2>{{ labels.sources }}</h2>
        <p>{{ labels.sourceNote }}</p>
        <ul>
          <li v-for="source in copy.sources" :key="source.url">
            <a :href="source.url" target="_blank" rel="noopener noreferrer">{{ source.label }} <span aria-hidden="true">↗</span></a>
          </li>
        </ul>
      </section>
      <footer class="guide-editorial">
        <p>{{ labels.editorial }}</p>
        <p>{{ labels.updated }} <time datetime="2026-10-12">2026-10-12</time></p>
      </footer>
    </div>
    <section class="guide-contact">
      <div class="guide-shell contact-inner">
        <div>
          <h2>{{ labels.contactHeading }}</h2>
          <p>{{ labels.contactText }}</p>
        </div>
        <div class="contact-actions">
          <router-link :to="$siteLink('/contact')" class="contact-button">{{ labels.contactAction }}</router-link>
          <router-link :to="$siteLink('/sms-login')" class="login-link">{{ labels.loginAction }}</router-link>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import BaseImg from '@/components/BaseImg.vue'
import PublicTransferGuide from '@/components/PublicTransferGuide.vue'
import { publicGuides } from '@/content/public-guides'

const pageLabels = {
  en: {
    home: 'Home',
    breadcrumbs: 'Breadcrumbs',
    related: 'Related information',
    faq: 'Frequently asked questions',
    sources: 'Official information',
    sourceNote: 'These official resources provide general information. They do not confirm Skyline’s business arrangements or the acceptance of a particular transaction.',
    editorial: 'Skyline website content editing. Confirm business and compliance facts with the team.',
    updated: 'Last updated:',
    contactHeading: 'Discuss your next step with Skyline',
    contactText: 'Tell our local team about your circumstances and proposed transaction. Confirm service availability, documents, the quote and payment arrangements before proceeding.',
    contactAction: 'Contact Skyline',
    loginAction: 'Log in to get started'
  },
  zh: {
    home: '首页',
    breadcrumbs: '面包屑导航',
    related: '相关信息',
    faq: '常见问题',
    sources: '官方资料来源',
    sourceNote: '以下官方资料提供一般信息，不代表已确认 Skyline 的具体业务安排或某笔交易的受理结果。',
    editorial: 'Skyline 网站内容编辑，业务／合规事实以团队确认为准。',
    updated: '最后更新：',
    contactHeading: '向 Skyline 咨询下一步',
    contactText: '告诉本地团队您的情况及拟办理交易，开始前确认服务是否适用、所需资料、报价和付款安排。',
    contactAction: '联系 Skyline',
    loginAction: '登录并开始办理'
  }
}

export default {
  components: { BaseImg, PublicTransferGuide },
  computed: {
    guide () {
      return publicGuides.find(page => page.path === this.$route.meta.publicPage)
    },
    locale () {
      return this.$i18n.locale === 'zh' ? 'zh' : 'en'
    },
    copy () {
      return this.guide && this.guide[this.locale]
    },
    labels () {
      return pageLabels[this.locale]
    },
    heroImage () {
      return ['/compliance', '/kyc-source-of-funds', '/security'].includes(this.guide.path) ? 'bg_about.png' : 'bg_products.png'
    },
    relatedHeadingId () {
      return this.guide.name + '-related'
    }
  }
}
</script>

<style scoped>
.public-guide { color: #163257; background: #fff; }
.public-guide ::v-deep .title { text-transform: none; }
.guide-shell { max-width: 1240px; margin: 0 auto; padding-right: clamp(24px, 6vw, 64px); padding-left: clamp(24px, 6vw, 64px); }
.guide-breadcrumbs { display: flex; align-items: center; flex-wrap: wrap; gap: 10px 14px; padding-top: 24px; padding-bottom: 24px; font-size: 15px; line-height: 1.6; color: #526172; }
.guide-breadcrumbs a { display: inline-flex; align-items: center; min-height: 36px; color: #163257; text-decoration: underline; text-underline-offset: 4px; }
.guide-intro { max-width: 820px; margin: 18px 0 44px; padding-left: 20px; border-left: 3px solid #bc9a41; font-size: clamp(18px, 1.6vw, 20px); line-height: 1.8; }
.guide-layout { display: grid; grid-template-columns: minmax(0, 1fr) 270px; gap: 64px; padding-bottom: 48px; }
.guide-body { min-width: 0; }
.guide-section { padding: 30px 0; border-top: 1px solid #d7dfe7; scroll-margin-top: 110px; }
.guide-section:first-child { padding-top: 0; border-top: 0; }
.guide-section:last-child { padding-bottom: 0; }
.guide-section p, .guide-section ul { max-width: 44em; }
.guide-section p:last-child { margin-bottom: 0; }
h2 { margin: 0 0 14px; color: #163257; font-size: clamp(23px, 2.2vw, 28px); line-height: 1.4; }
h3 { margin: 0 0 14px; color: #163257; font-size: 21px; line-height: 1.45; }
p, li { font-size: 17px; line-height: 1.85; }
p { margin: 0 0 18px; }
ul { padding-left: 22px; margin: 18px 0 0; }
li { padding-left: 2px; margin-bottom: 10px; }
.guide-section ul { list-style: none; padding: 0; margin-top: 14px; }
.guide-section li { position: relative; margin: 0; padding: 9px 0 9px 22px; border-bottom: 1px solid #e6ebf0; line-height: 1.7; }
.guide-section li:last-child { border-bottom: 0; }
.guide-section li::before { content: ""; position: absolute; left: 2px; top: 1.15em; width: 8px; height: 8px; border-radius: 50%; background: #bc9a41; }
.guide-related { align-self: start; position: sticky; top: 110px; padding: 22px 24px; background: #f5f5f5; border-top: 3px solid #bc9a41; border-radius: 4px; }
.guide-related h2 { font-size: 22px; }
.guide-related ul { margin: 0; padding: 0; list-style: none; }
.guide-related li { margin-bottom: 16px; padding: 0; }
.guide-related a { display: inline-block; color: #163257; text-decoration: underline; text-underline-offset: 4px; font-size: 16px; line-height: 1.7; }
.guide-related span { margin-left: 6px; }
.guide-faq { padding-top: 48px; padding-bottom: 48px; background: #f5f5f5; }
.faq-items { max-width: 900px; margin-top: 28px; }
.faq-item { margin-bottom: 26px; padding-bottom: 26px; border-bottom: 1px solid #d7dfe7; }
.faq-item:last-child { margin-bottom: 0; padding-bottom: 0; border-bottom: 0; }
.faq-item p { margin-bottom: 0; }
.guide-sources { padding-top: 44px; padding-bottom: 28px; }
.guide-sources h2 { font-size: 25px; }
.guide-sources p { font-size: 16px; color: #526172; }
.guide-sources a { color: #163257; text-decoration: underline; text-underline-offset: 4px; overflow-wrap: anywhere; }
.guide-editorial { margin-bottom: 44px; padding-top: 20px; border-top: 1px solid #d7dfe7; color: #526172; }
.guide-editorial p { font-size: 14px; margin-bottom: 6px; }
.guide-contact { background: #163257; color: #fff; }
.contact-inner { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 44px; align-items: center; padding-top: 48px; padding-bottom: 48px; }
.guide-contact h2 { color: #fff; font-size: clamp(26px, 2.8vw, 36px); }
.guide-contact p { max-width: 720px; margin-bottom: 0; }
.contact-actions { display: flex; flex-direction: column; align-items: center; gap: 20px; }
.contact-button { display: inline-flex; align-items: center; justify-content: center; min-height: 48px; padding: 12px 30px; border-radius: 30px; color: #163257; background: #bc9a41; font-size: 17px; font-weight: 600; line-height: 1.5; text-align: center; }
.contact-button:hover { color: #163257; background: #d0b15e; }
.login-link { color: #fff; font-size: 16px; line-height: 1.7; text-decoration: underline; text-underline-offset: 5px; }
a:focus-visible { text-decoration-thickness: 3px; text-underline-offset: 6px; }
@media (max-width: 960px) {
  .guide-layout { grid-template-columns: minmax(0, 1fr); gap: 36px; }
  .guide-related { position: static; }
  .guide-related ul { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px 24px; }
  .guide-related li { margin-bottom: 0; }
  .contact-inner { grid-template-columns: minmax(0, 1fr); gap: 28px; }
  .contact-actions { align-items: flex-start; }
}
@media (max-width: 540px) {
  .guide-breadcrumbs { padding-top: 18px; padding-bottom: 18px; }
  .guide-intro { margin-top: 12px; margin-bottom: 32px; font-size: 19px; }
  .guide-section { padding: 24px 0; }
  p, li { font-size: 16px; }
  .guide-related ul { grid-template-columns: minmax(0, 1fr); gap: 14px; }
  .guide-faq { padding-top: 36px; padding-bottom: 36px; }
  .guide-sources { padding-top: 36px; }
  .contact-inner { padding-top: 36px; padding-bottom: 36px; }
}
</style>
